/**
 * Image URL helpers — let the frontend request right-sized images from the
 * backend instead of the original (often multi-MB) uploads.
 *
 * Usage:
 *   import { mediaUrl } from '@/lib/imageUrl';
 *   <img src={mediaUrl(product.image, 640)} loading="lazy" />
 *
 * Behavior:
 *   * /api/media/files/X    → /api/media/files/X?w=640&q=80
 *   * optional `format`      → adds fmt=webp (used by event-card artwork)
 *   * Unsplash               → existing query merged with w=, q=, fit=crop
 *   * Pexels                 → ?w= appended (Pexels honors it)
 *   * YouTube thumbs         → choose mqdefault/hqdefault based on width
 *   * Anything else          → returned untouched
 *
 * `width` accepts any int — the backend snaps to a fixed bucket so CDN
 * cache hit-rate stays high.
 */

const isOurMedia = (url) => /\/api\/media\/files\//.test(url || '');
const isUnsplash = (url) => /images\.unsplash\.com/.test(url || '');
const isPexels = (url) => /images\.pexels\.com/.test(url || '');
const isYouTubeThumb = (url) => /i\.ytimg\.com\/vi\//.test(url || '');

export const canonicalMediaUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  const match = url.match(/^(?:https?:\/\/[^/\s]+)?\/(?:api\/media\/files|uploads)\/([A-Za-z0-9][A-Za-z0-9_.-]*)(\?[^#\s]*)?(#.*)?$/i);
  if (!match) return url;
  if (/^https?:/i.test(url) && /\/uploads\//i.test(url)) {
    try {
      const host = new URL(url).hostname.toLowerCase();
      if (!['rastyle', 'emergentagent', 'localhost', '127.0.0.1'].some((marker) => host.includes(marker))) return url;
    } catch {
      return url;
    }
  }
  return `/api/media/files/${match[1]}${match[2] || ''}${match[3] || ''}`;
};

const appendOrUpdateParam = (url, key, value) => {
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.set(key, value);
    return u.toString();
  } catch {
    // Relative URL — build manually.
    const sep = url.includes('?') ? '&' : '?';
    const re = new RegExp(`([?&])${key}=[^&]*`);
    if (re.test(url)) return url.replace(re, `$1${key}=${value}`);
    return `${url}${sep}${key}=${value}`;
  }
};

const ytSizeForWidth = (w) => {
  // YouTube exposes fixed-size thumbs only; pick the smallest that covers `w`.
  if (w <= 120) return 'default';         // 120x90
  if (w <= 320) return 'mqdefault';       // 320x180
  if (w <= 480) return 'hqdefault';       // 480x360
  if (w <= 640) return 'sddefault';       // 640x480
  return 'maxresdefault';                 // 1280x720
};

export const mediaUrl = (url, width = 800, quality = 80, format = '') => {
  if (!url || typeof url !== 'string') return url;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  url = canonicalMediaUrl(url);

  if (isOurMedia(url)) {
    let out = appendOrUpdateParam(url, 'w', width);
    out = appendOrUpdateParam(out, 'q', quality);
    if (format) out = appendOrUpdateParam(out, 'fmt', format);
    return out;
  }
  if (isUnsplash(url)) {
    let out = appendOrUpdateParam(url, 'w', width);
    out = appendOrUpdateParam(out, 'q', quality);
    out = appendOrUpdateParam(out, 'auto', 'format');
    return out;
  }
  if (isPexels(url)) {
    return appendOrUpdateParam(url, 'w', width);
  }
  if (isYouTubeThumb(url)) {
    // Replace the size token in the URL with the appropriate file
    // (default | mqdefault | hqdefault | sddefault | maxresdefault).
    const size = ytSizeForWidth(width);
    return url.replace(
      /\/(default|mqdefault|hqdefault|sddefault|maxresdefault)\.jpg/,
      `/${size}.jpg`,
    );
  }
  return url;
};

/** Convenience: tiny thumb (for grid items). */
export const thumbUrl = (url) => mediaUrl(url, 320, 75);

/** Convenience: hero / above-the-fold full-width image. */
export const heroUrl = (url) => mediaUrl(url, 1280, 82);

export default mediaUrl;
